import Farmer from "../images/Compressed/farmer.jpg";
import Manufacturing from "../images/Compressed/manufacturing.jpg";
import Farming from "../images/Compressed/farming.jpg";
import FNSHero from "../images/Compressed/FNS.jpg";
import SFFWHero from "../images/Compressed/sffw-lg.jpg";
import MCCLEPHero from "../images/Compressed/mcclep2.jpg";
import LEHero from "../images/Compressed/officer.shadow.jpg";
import ASHero from "../images/Compressed/srt2.jpg";
import Gov from "../images/Compressed/gov.jpg";

import usaidPeople from "../images/Compressed/usaidpeople.jpg";
import usaidAffinity from "../images/Compressed/usaidaffinity.jpg";
import usaidProcess from "../images/Compressed/usaidprocess.jpg";

import dcmaMural from "../images/Compressed/dcmamural.jpg";
import dcmaBoard from "../images/Compressed/ibidsboard.png";
import dcmaMap from "../images/Compressed/ibidsmap.jpg";

import nassData from "../images/NASS/datacows.jpg";
import nassPipes from "../images/NASS/pipes.jpg";
import nassHandoff from "../images/NASS/handoff.jpg";

import fnsTimeline from "../images/FNS/timeline.jpg";
import fnsWorkflow from "../images/FNS/workflow.jpg";
import fnsMap from "../images/FNS/map.jpg";
import connections from "../images/Compressed/connections.jpg";
import bwcArduino from "../images/bwc/JPEG/arduino.jpg";
import bwcScreenshot from "../images/bwc/JPEG/screenshot.jpg";
import bwcTest from "../images/bwc/JPEG/test.jpg";

export const caseStudies = {
  DeloitteAI: {
    slug: "DeloitteAI",
    header: {
      descr: "PRODUCT OWNER",
      location: "@ DELOITTE",
      time: "2025 - 2026",
    },
    category: "AI-driven transformation",
    heroImage: connections,
    title: "AI-Driven Personalization Platform",

    overview: {
      organization: "National non-profit organization (via Deloitte)",
      product: "AI-driven personalization and engagement platform",
      timeframe: "2025 - 2026",
      role: "Product Owner, AI Product Strategy & Design",
      intro:
        "Centralized and operationalized AI-driven personalization to unify fragmented outreach and improve engagement across channels.",
      summary:
        "Led product strategy and experience design for an AI-driven personalization platform built to reverse membership decline by replacing fragmented communications with coordinated, relevance-driven engagement across channels.",
    },
    problem: [
      "The organization was experiencing steady membership decline, alongside weakening engagement across digital channels. While engagement was not critically low, it was inconsistent and inefficient—users often found a single piece of content and disengaged, with limited session depth or sustained interaction.",

      "Communication strategy was fragmented across teams and platforms. Email, SMS, and mobile push campaigns were managed independently, resulting in high message volume, overlapping outreach, and no centralized coordination. Members frequently received irrelevant or redundant messaging, leading to email fatigue and declining responsiveness, with average click-through rates hovering around 2–3%.",

      "The organization lacked a unified understanding of its users and their interests. Targeting capabilities were limited, requiring reliance on static lists generated by data teams, which reduced marketer agility and forced broad, inefficient campaign sends. As a result, experiences across web, app, and outreach channels remained largely generic—failing to adapt to individual user needs, behaviors, or intent.",
    ],
    challenge: [
      "The initiative required a fundamental shift in how the organization operated. Teams that previously owned their channels end-to-end were now being asked to participate in a shared system where AI models influenced what content users would see. This created tension around control, as each team viewed their messaging as critical and was hesitant to relinquish autonomy to a centralized, model-driven approach.",

      "Trust in the system became a core challenge. When content did not surface as expected, teams often interpreted it as a failure of the model rather than a signal of content relevance. This led to resistance, demands for increased manual control, and skepticism toward AI-driven decision-making. Navigating this required reframing the role of the models while preventing overcorrection that would reduce their effectiveness.",

      "The work carried significant organizational risk. As a non-profit, the organization had to maintain a consistent and responsible public voice while introducing dynamic, personalized experiences. The success of the initiative was tied directly to reversing membership decline and improving engagement—making it a high-stakes investment with visible consequences if it failed to deliver.",

      "Operational complexity added additional pressure. The platform was launched quickly to begin collecting data, requiring continuous iteration in production. Data quality issues from upstream content systems, varying stakeholder expectations, and the need to balance automation with appropriate human oversight created an environment where nearly every issue was treated as urgent. The challenge was not just building the system, but stabilizing and evolving it while maintaining stakeholder confidence.",
    ],
    responsibilities: [
      "Owned product vision, strategy, and roadmap for the personalization platform, acting as the primary interface between business stakeholders and delivery teams. Led requirement intake, defined priorities, and ensured all capabilities aligned with long-term strategic direction.",

      "Managed the engineering backlog and drove day-to-day product decisions, including feature prioritization, scope control, and trade-off decisions. Regularly engaged senior leadership to present recommendations, align on KPIs, and guide strategic direction.",

      "Served as the central coordination point across data science, engineering, marketing, and leadership teams—translating between model capabilities, platform constraints, and business needs to ensure cohesive system behavior and delivery progress.",

      "Defined and governed how AI-generated recommendations were operationalized across channels, including web, mobile, email, SMS, and push messaging. Designed filtering logic, business rules, and delivery mechanisms to ensure relevance, appropriateness, and consistency of user experiences.",

      "Led the design and evolution of internal platform tools within Foundry, improving usability and enabling marketers to manage campaigns, audiences, and outreach more effectively without reliance on technical teams.",

      "Established core system structures, including data models, ontology design, targeting logic, and engagement rules—balancing automation with necessary human control to meet organizational, regulatory, and brand requirements.",

      "Directed QA, UAT, and post-launch iteration, including defect triage, stakeholder onboarding, and ongoing system refinement. Acted as a primary advocate for the product strategy, frequently reframing perceived issues and guiding stakeholders toward a clearer understanding of system behavior.",
    ],
    approach: [
      "I structured the solution around a centralized engagement architecture using Palantir Foundry’s ontology to unify content, user data, and outreach operations. Content was ingested from a shared value library and routed through multiple recommender models, each optimized for different content types and success criteria such as engagement or action completion.",

      "The experience was designed around two modes of recommendation. Known users received personalized content based on behavioral embeddings, while unknown users were served popularity-based recommendations derived from similar audience patterns. This ensured the system could deliver relevant experiences across both identified and anonymous traffic.",

      "To operationalize this across channels, I led the design of a custom campaign and outreach management layer within Foundry. This enabled marketers to manage email, SMS, push messaging, and web experiences in a single system, while also providing controlled override mechanisms for priority communications when needed.",

      "The platform was launched rapidly as an MVP to begin generating real behavioral data, with iteration driven by live usage rather than assumptions. Performance signals from analytics and campaign outcomes fed back into both product decisions and model refinement, creating a continuous feedback loop between user behavior and system evolution.",

      "Stakeholder alignment was managed through frequent demos, structured releases, and ongoing education. Each capability rollout was paired with clear communication—either through job aids or release notes—to build familiarity and gradually increase trust in the system’s behavior.",

      "At its core, the approach required balancing automation with control. The initial principle was to trust model-driven recommendations, but this evolved into a more nuanced framework—identifying where automation created value and where human oversight was necessary to meet organizational, regulatory, and brand requirements.",
    ],
    contributions: [
      "Introduced a user-state framework that enabled the system to dynamically adapt content and outreach based on a user’s relationship to the organization (e.g., prospect vs. member). This ensured relevance across experiences while reducing unnecessary model computation and preventing misaligned messaging.",

      "Established an omni-channel activation model that unified how recommendations were delivered across web, app, email, SMS, and push messaging—creating a consistent, coordinated engagement layer rather than isolated channel execution.",

      "Designed and implemented behavior- and interest-based audience targeting capabilities, allowing marketers to segment users based on engagement patterns, recency, and subject matter interest. This replaced static list-based targeting with a more adaptive and data-driven approach.",

      "Bridged the gap between automated personalization and marketer control by introducing mechanisms that allowed teams to influence subject prioritization and campaign direction while still leveraging model-driven recommendations.",

      "Enabled the foundation for propensity modeling by structuring engagement data and user signals in a way that supported downstream predictions such as likelihood to convert, engage, or enter specific user journeys.",

      "Improved system effectiveness and stakeholder adoption by deeply integrating with the Foundry platform—understanding its ontology, data flows, and constraints—while aligning those capabilities with real organizational needs and workflows.",

      "Acted as an embedded partner within the organization, building trust and alignment by operating as part of the client team. This enabled more effective change management, faster decision-making, and smoother adoption of new capabilities.",
    ],
    outcomes: [
      "Personalization significantly expanded reach and engagement across the organization’s digital ecosystem. Unique personalization engagers grew 141% year-over-year, increasing from 2M to over 5M users, while also driving 30K new site registrations and email permissions through more relevant, targeted experiences.",

      "Users exposed to personalized experiences demonstrated materially stronger engagement. On web, these users visited 2.1x more pages, clicked 3.2x more, and returned 50% more frequently than non-personalized users—indicating deeper interaction and sustained interest.",

      "Channel-specific improvements reinforced the effectiveness of the approach. Email personalization increased page views and clicks by 1.3x and drove a 59% higher return visit rate among prior engagers, while mobile app personalization contributed to a 46% increase in engaged users year-over-year.",

      "The platform began to show measurable impact on core organizational outcomes. Same-session membership renewals increased 12% year-over-year among personalization-engaged users (+3.7K renewals), signaling early progress toward reversing membership decline and improving long-term retention.",

      "Personalized experiences also improved overall satisfaction and brand perception. Users engaging with personalized content reported higher Net Promoter Score (NPS) and customer satisfaction (CSAT), while increased engagement across key pages contributed to greater advertising exposure and revenue opportunities.",

      "Beyond performance metrics, the organization gained new capabilities in how it engages its audience. For the first time, teams could coordinate outreach across channels, target users based on behavior and interest, and continuously optimize experiences using real-time data—shifting from static campaigns to a more adaptive, learning system.",
    ],
  },

  USAID: {
    slug: "USAID",
    header: {
      descr: "DESIGN LEAD",
      location: "@ STEAMPUNK",
      time: "2022 - 2023",
    },
    category: "Enterprise transformation",
    heroImage: Farmer,
    title: "USAID: Partner Relationship Management System",
    overview: {
      organization: "U.S. Agency for International Development",
      product: "Partner Relationship Management System",
      timeframe: "2022 - 2023",
      role: "Design Lead, Service Design & Product Strategy",
      intro:
        "Designed a unified CRM approach to improve visibility and coordination of partner relationships across a global organization.",
      summary:
        "Led service design and product strategy for an enterprise CRM initiative intended to give USAID a shared view of partner relationships, knowledge, and engagement activity across headquarters and global missions.",
    },
    problem: [
      "USAID managed critical private-sector and development partnerships across distributed teams, regions, and operating models. Relationship knowledge lived in inconsistent formats, making it hard to understand who was engaging which partners, where duplication existed, and where strategic opportunities were being missed.",
      "The program existed to replace fragmented practices with a coordinated system that could improve institutional memory, make collaboration easier, and support faster decision-making at both mission and agency levels.",
    ],
    challenge: [
      "The work required aligning leadership expectations with the realities of how missions, headquarters teams, and field staff actually managed relationships in different contexts.",
      "The complexity was not only technical. It was organizational: driving standardization without erasing local flexibility, building trust in a new workflow, and shaping a product that users would consistently contribute to.",
    ],
    responsibilities: [
      "Led the design workstream and set the human-centered strategy for discovery, synthesis, and concept development.",
      "Defined the research plan, identified priority stakeholders with leadership, and drove interviews across user and executive groups.",
      "Aligned designers, change management support, and client stakeholders around a shared product direction and adoption strategy.",
      "Shaped testing and QA conversations by advocating for user priorities during decision-making and release planning.",
    ],
    approach: [
      "I used stakeholder interviews, workflow mapping, and collaborative synthesis to understand how relationship management actually worked across the agency. That gave the team a grounded view of where information broke down and where a CRM could create leverage.",
      "From there, I translated research into design principles, product requirements, and testing structures. I also formed an advisory user group so the product direction stayed tied to real operating needs instead of abstract requirements.",
    ],
    contributions: [
      "Defined the research and engagement strategy that brought agency leadership and frontline users into the same product conversation.",
      "Created a cross-agency view of pain points, needs, and workflow variations to inform feature prioritization and governance decisions.",
      "Established an advisory user group to create repeatable feedback loops and reduce downstream delivery risk.",
      "Translated qualitative insights into product direction, experience concepts, and QA priorities that improved fit-to-need.",
    ],
    outcomes: [
      "Enabled a clearer agency-wide operating model for capturing and sharing partner relationship knowledge.",
      "Improved alignment between leadership goals for strategic visibility and user needs for practical, low-friction workflows.",
      "Reduced adoption risk by validating decisions with representative users before delivery and during QA.",
      "Positioned the CRM as an enterprise coordination tool rather than just another system of record.",
    ],
    artifacts: [
      {
        title: "Stakeholder landscape",
        description:
          "This visual mapped the different user environments across headquarters and field missions, helping frame why a single workflow could not simply be imposed from the center.",
        impact:
          "It supported leadership conversations about standardization, governance, and change management.",
        image: usaidPeople,
        alt: "USAID stakeholder landscape",
      },
      {
        title: "Pain point synthesis",
        description:
          "This artifact captured themes from discovery across teams and exposed the operational friction behind fragmented relationship management.",
        impact:
          "It helped connect qualitative research to concrete feature and process decisions.",
        image: usaidAffinity,
        alt: "USAID pain point synthesis",
      },
      {
        title: "Future-state process framing",
        description:
          "This process view translated research into a future-state service model for how partner information could move through the organization.",
        impact:
          "It gave the team a shared narrative for product direction and adoption planning.",
        image: usaidProcess,
        alt: "USAID future-state process",
      },
    ],
    reflection:
      "Enterprise tools succeed when they respect local operating realities while still creating shared visibility. This project reinforced that adoption strategy is product strategy, especially in globally distributed organizations.",
  },
  DCMA: {
    slug: "DCMA",
    header: {
      descr: "DESIGN LEAD",
      location: "@ STEAMPUNK",
      time: "2021 - 2022",
    },
    category: "Enterprise transformation",
    heroImage: Manufacturing,
    title: "Defense Contract Management Agency Modernization",
    overview: {
      organization: "Defense Contract Management Agency",
      product: "Multi-system modernization and analytics initiative",
      timeframe: "2021 - 2022",
      role: "Design Lead, Product & Delivery Leadership",
      intro:
        "Led design and delivery across multiple modernization efforts to improve operational visibility and system usability.",
      summary:
        "Led design across several modernization efforts for DCMA, shaping discovery, roadmap decisions, and delivery for systems intended to improve operational visibility, analytics, and user workflows.",
    },
    problem: [
      "DCMA relied on aging applications and fragmented workflows across business functions. That limited visibility into mission-critical work, created manual effort, and slowed the agency’s ability to act on operational data.",
      "The modernization effort existed to replace disconnected tools with more usable, analyzable, and sustainable systems aligned to the agency’s current technical environment.",
    ],
    challenge: [
      "The portfolio involved multiple applications, different user groups, and evolving technical direction, including migration away from Salesforce toward Microsoft Power Platform.",
      "The work also required balancing strategy and delivery under lean staffing. Early in the effort, I was simultaneously leading design, coordinating client engagement, and helping manage product and development activities for one of the applications.",
    ],
    responsibilities: [
      "Led discovery and design across the modernization portfolio, setting engagement criteria, timelines, and deliverables for each workstream.",
      "Drove stakeholder engagement, technical discovery, backlog refinement, and requirements shaping with client teams.",
      "Served as a primary point of contact for one application, running standups, managing priorities, and coordinating delivery decisions.",
      "Aligned user research, product direction, UAT feedback, and release preparation to keep modernization efforts moving.",
    ],
    approach: [
      "I scaled the design approach based on system complexity, user-base size, and delivery risk. Each engagement combined stakeholder interviews, process mapping, technical discovery, and iterative requirements refinement.",
      "Because the initiative was moving across platforms and across multiple systems, I focused on creating enough structure for decisions to stick: shared roadmaps, prioritized backlogs, validated requirements, and visible checkpoints through testing.",
    ],
    contributions: [
      "Defined discovery and design plans for multiple application modernization efforts across the DCMA portfolio.",
      "Led research and stakeholder alignment that connected user pain points to system priorities and release planning.",
      "Helped steer a Salesforce-to-Power Platform migration by translating expected experience patterns into build-ready direction.",
      "Managed UAT feedback and iterative refinement so emerging defects and usability gaps were addressed before release.",
    ],
    outcomes: [
      "Advanced several systems into late-stage UAT, with one application fully deployed during my time on the program.",
      "Improved the agency’s ability to capture feedback, monitor work, and make faster decisions using more accessible analytics.",
      "Reduced fragmentation by aligning product decisions to a modern platform strategy rather than continuing one-off application growth.",
      "Enabled broader and more secure insight collection, including workflows that incorporated public input where needed.",
    ],
    artifacts: [
      {
        title: "Engagement roadmap",
        description:
          "This planning view showed how discovery, alignment, and delivery activities sequenced for a modernization workstream.",
        impact:
          "It helped set expectations with stakeholders and provided a repeatable model for future engagements.",
        image: dcmaBoard,
        alt: "DCMA engagement roadmap",
      },
      {
        title: "User-centered synthesis",
        description:
          "This artifact represented the team’s investment in understanding users across the portfolio before pushing solutions forward.",
        impact:
          "It kept modernization anchored in adoption and mission fit rather than just technical replacement.",
        image: dcmaMural,
        alt: "DCMA user research mural",
      },
      {
        title: "Analytics and data relationships",
        description:
          "This visual supported the conversation around making complex data relationships visible through reporting and dashboards.",
        impact:
          "It connected modernization work to better operational insight for agency leaders and staff.",
        image: dcmaMap,
        alt: "DCMA analytics mapping",
      },
    ],
    reflection:
      "Leading modernization at portfolio level requires switching constantly between service design, product management, and delivery orchestration. The work reinforced how important it is to create decision structures early when systems, teams, and technical direction are all moving.",
  },
  NASS: {
    slug: "NASS",
    header: {
      descr: "DESIGN LEAD",
      location: "@ STEAMPUNK",
      time: "2020 - 2021",
    },
    category: "Enterprise transformation",
    heroImage: Farming,
    title: "USDA: National Agricultural Statistics Service",
    overview: {
      organization: "USDA National Agricultural Statistics Service",
      product: "360° data ecosystem assessment and modernization roadmap",
      timeframe: "2020 - 2021",
      role: "Design Lead, Service Design & Front-End Product Support",
      intro:
        "Mapped and modernized a complex data ecosystem to improve survey operations, integration, and reporting.",
      summary:
        "Led discovery and design for a broad assessment of NASS’s data ecosystem, then carried those insights into delivery work on tools intended to streamline survey operations, issue resolution, and reporting.",
    },
    problem: [
      "NASS depended on a large, evolving ecosystem of survey, reporting, and data-management tools to support U.S. agricultural statistics. Over time, the environment had become difficult to navigate, difficult to integrate, and burdensome for both internal teams and respondents.",
      "The project existed to identify where the ecosystem created friction, define a more coherent future state, and improve how agricultural data could be collected, managed, and analyzed.",
    ],
    challenge: [
      "The work touched multiple repositories, specialized applications, survey channels, and user groups across the survey lifecycle.",
      "It required thinking at both system and service level: the respondent experience, internal operating processes, data interoperability, acquisition planning, and the constraints of systems that could not all be replaced at once.",
    ],
    responsibilities: [
      "Led discovery into the current data ecosystem, stakeholder workflows, and operational pain points across agency practices.",
      "Mapped current and future-state processes, dependencies, and integration opportunities to support modernization decisions.",
      "Carried discovery insight into later delivery phases by supporting front-end design and development in Salesforce Service Cloud.",
      "Helped shape backlog priorities, QA, and UAT based on a deep understanding of user and business needs.",
    ],
    approach: [
      "I combined interviews, technical research, process mapping, and service design artifacts to make the ecosystem legible for both the team and the client. The goal was to show not just what systems existed, but how work and data actually moved through them.",
      "As the engagement shifted toward delivery, I used that systems understanding to guide product choices, prioritize workflow improvements, and maintain continuity between strategy and implementation.",
    ],
    contributions: [
      "Built a shared view of the NASS data ecosystem that exposed operational friction, handoff failures, and integration gaps.",
      "Defined future-state process concepts that supported better survey coordination, issue resolution, and data reuse.",
      "Bridged strategy and execution by applying discovery insight directly to front-end product work and testing.",
      "Advised on modernization direction that reduced ecosystem complexity instead of adding more siloed tooling.",
    ],
    outcomes: [
      "Enabled an agency-wide survey calendar that improved coordination and helped reduce respondent burden.",
      "Delivered a structured issue submission and tracking capability that improved internal routing and resolution workflows.",
      "Supported movement toward reusable survey question management and more consistent survey generation across channels.",
      "Improved access to insights by consolidating data where possible and connecting critical systems into more usable reporting flows.",
    ],
    artifacts: [
      {
        title: "Data management framing",
        description:
          "This artifact helped show where data normalization and retrieval issues were creating downstream operational friction.",
        impact:
          "It supported conversations about where system redesign and governance would create the most value.",
        image: nassData,
        alt: "NASS data management artifact",
      },
      {
        title: "Integration planning",
        description:
          "This visual represented the challenge of moving data across systems with different standards and constraints.",
        impact:
          "It helped reframe modernization around interoperability rather than isolated tool improvements.",
        image: nassPipes,
        alt: "NASS integration planning artifact",
      },
      {
        title: "Change management lens",
        description:
          "This artifact supported discussions about shifting away from ad hoc solutioning toward a more deliberate acquisition and modernization model.",
        impact:
          "It tied systems decisions to organizational behavior and governance.",
        image: nassHandoff,
        alt: "NASS change management artifact",
      },
    ],
    reflection:
      "The most valuable design work in large ecosystems is often translational: helping stakeholders see the full operating picture clearly enough to make better decisions. This project sharpened that capability.",
  },
  FNS: {
    slug: "FNS",
    header: {
      descr: "DESIGN LEAD",
      location: "@ STEAMPUNK",
      time: "2021 - 2022",
    },
    category: "Enterprise transformation",
    heroImage: FNSHero,
    title: "USDA Food & Nutrition Service: Employment & Training Data System",
    overview: {
      organization: "USDA Food & Nutrition Service",
      product: "SNAP Employment & Training data system strategy",
      timeframe: "2021 - 2022",
      role: "Design Lead, Service Design & Product Strategy",
      intro:
        "Defined the future-state design and strategy for a national reporting system supporting SNAP Employment & Training programs.",
      summary:
        "Led discovery and future-state design for a new data system supporting SNAP Employment & Training reporting, translating assessment findings into product direction, procurement guidance, and a clearer modernization path.",
    },
    problem: [
      "FNS needed a system that could support more consistent reporting, analysis, and decision-making for SNAP Employment & Training programs across federal, regional, and state stakeholders.",
      "Existing reporting processes were fragmented, manual, and difficult to scale. The project existed to bridge the gap between an earlier assessment and a concrete plan for what the future system should do and how it should be procured.",
    ],
    challenge: [
      "The work spanned national, regional, and state perspectives, each with different responsibilities, constraints, and reporting realities.",
      "Beyond technology selection, the challenge was designing a system concept that could reduce burden, support interoperability, and make complex reporting data easier to understand and act on.",
    ],
    responsibilities: [
      "Led discovery into current reporting processes and stakeholder needs across FNS and regional representatives.",
      "Defined business and user requirements by combining previous assessment findings with live stakeholder input.",
      "Created future-state workflows, site maps, and service models to evaluate technology options and shape the procurement path.",
      "Drove a human-centered assessment of solution fit so technical recommendations stayed grounded in operational reality.",
    ],
    approach: [
      "I started by reviewing the prior assessment, then used interviews and collaborative discussions to understand how reporting worked in practice and where pain points differed across roles.",
      "I translated those insights into future-state artifacts that let the team compare technology options against real user workflows, reporting cycles, and decision needs rather than abstract requirements alone.",
    ],
    contributions: [
      "Connected existing assessment findings to live stakeholder insight so the program had a more actionable foundation for modernization.",
      "Mapped the annual reporting lifecycle and user workflows to reveal where manual effort, bottlenecks, and communication gaps were slowing work.",
      "Produced future-state information architecture and process concepts that supported platform evaluation and procurement planning.",
      "Defined design principles that clarified how the eventual system should reduce burden, tell a clearer story with the data, and improve collaboration.",
    ],
    outcomes: [
      "Delivered a workplan and roadmap that gave FNS a clearer, approved path toward building the new system.",
      "Improved alignment around how technology should automate validation, surface analytics, and guide users through reporting work.",
      "Enabled stronger decision-making about platform fit by grounding recommendations in service design artifacts and user workflows.",
      "Positioned the effort for implementation with a clearer product vision rather than a purely technical procurement exercise.",
    ],
    artifacts: [
      {
        title: "Reporting lifecycle map",
        description:
          "This artifact showed how reporting activity unfolded over time and where different users encountered friction.",
        impact:
          "It helped identify bottlenecks and future-state improvement opportunities.",
        image: fnsTimeline,
        alt: "FNS reporting lifecycle map",
      },
      {
        title: "User workflow model",
        description:
          "This workflow translated manual stakeholder practices into a more coherent digital service flow.",
        impact:
          "It was used to validate whether candidate platforms could support the right behaviors and timing.",
        image: fnsWorkflow,
        alt: "FNS user workflow model",
      },
      {
        title: "Notional app structure",
        description:
          "This site map organized large volumes of E&T data into a more navigable structure for co-creation with the client.",
        impact:
          "It helped the team compare platform options and refine a future-state user experience.",
        image: fnsMap,
        alt: "FNS app structure",
      },
    ],
    reflection:
      "Advisory work creates the most value when it gives clients a usable decision framework, not just a report. This engagement showed how service design can materially improve procurement and platform strategy.",
  },
  SFFW: {
    slug: "SFFW",
    header: {
      descr: "DESIGN STRATEGIST & GRAPHIC DESIGNER",
      location: "@ HQ USMC - SFFW",
      time: "2015 - 2018",
    },
    category: "Foundational experience",
    heroImage: SFFWHero,
    title: "San Francisco Fleet Week",
    overview: {
      organization: "San Francisco Fleet Week / HQ USMC",
      product: "Integrated event communications and campaign design",
      timeframe: "2015 - 2018",
      role: "Design Strategist, Visual Design & Media Lead",
      intro:
        "Directed design and media production for a large-scale public event, creating cohesive campaigns across channels.",
      summary:
        "Led creative direction and production across event branding, campaign assets, photography, and video for one of the country’s highest-profile Fleet Week programs, helping turn complex public-facing needs into cohesive communications.",
    },
    problem: [
      "San Francisco Fleet Week needed a consistent and high-impact communications presence across public events, promotional campaigns, and partner-facing materials.",
      "The work mattered because the program operated at significant scale and public visibility, requiring materials that could support attendance, sponsorship, education, and trust in the event experience.",
    ],
    challenge: [
      "The program combined live events, public education, brand stewardship, and multi-format media production under tight timelines.",
      "Success required balancing creativity with operational execution while producing work that could hold up across print, digital, social, environmental, and event contexts.",
    ],
    responsibilities: [
      "Led visual design and media production for campaign materials supporting Fleet Week events and partner engagement.",
      "Directed photography and video capture on-site, then translated that material into promotional and commercial assets.",
      "Shaped how the event was presented to the public through cohesive creative systems across multiple channels.",
      "Coordinated execution under event-driven deadlines where timing, clarity, and consistency directly affected public reach.",
    ],
    approach: [
      "I treated each campaign cycle as both a design problem and an operating problem: understand the audience, identify the event moments that mattered most, and build a visual system that could scale across formats quickly.",
      "That meant combining field capture, rapid content production, and practical brand decision-making so the event narrative stayed coherent from pre-event promotion through live activation.",
    ],
    contributions: [
      "Created integrated campaign assets that gave the event a stronger and more consistent public-facing identity.",
      "Directed and produced photography and video content used across promotion, event storytelling, and partner materials.",
      "Improved continuity between live event experience and digital communications by designing with cross-channel reuse in mind.",
      "Built foundational experience in translating large, complex programs into clear public narratives.",
    ],
    outcomes: [
      "Enabled a more cohesive communications presence for a large-scale public event with national visibility.",
      "Improved the quality and consistency of promotional materials across channels and event cycles.",
      "Strengthened storytelling around the event’s public mission, educational value, and partner participation.",
      "Built foundational leadership skills in managing complex creative execution under real operational pressure.",
    ],
    reflection:
      "This work was foundational because it taught me how much good strategy depends on clear narrative design. That lesson continues to shape how I position products, services, and transformation work today.",
  },
  MCCLEP: {
    slug: "MCCLEP",
    header: {
      descr: "DESIGN STRATEGIST & DEVELOPER",
      location: "@ HSSI / HQ USMC",
      time: "2013 - 2019",
    },
    category: "Foundational experience",
    heroImage: MCCLEPHero,
    title: "Marine Corps Civilian Law Enforcement Program",
    overview: {
      organization: "Headquarters U.S. Marine Corps",
      product: "Civilian law enforcement recruiting and communications program",
      timeframe: "2013 - 2019",
      role: "Design Strategist, Developer & Media Lead",
      intro:
        "Built and delivered digital, media, and communication tools supporting recruiting and public engagement initiatives.",
      summary:
        "Owned a broad mix of communications, web, and media work supporting the Marine Corps Civilian Law Enforcement Program, building foundational experience in translating complex institutional needs into public-facing products and campaigns.",
    },
    problem: [
      "The program needed stronger recruiting and communication tools to reach target audiences through events, web experiences, and media assets.",
      "This work mattered because the program depended on clear public communication, compelling materials, and reliable digital entry points for candidates and stakeholders.",
    ],
    challenge: [
      "The scope was unusually broad, spanning campaign design, photography, video production, e-learning, web design, and intake tooling.",
      "That created a practical challenge of moving between strategy, production, and implementation while keeping outputs cohesive and mission-aligned.",
    ],
    responsibilities: [
      "Led production of campaign and recruiting materials used in job fairs, public displays, social channels, and video distribution.",
      "Designed and developed web experiences that aligned with Marine Corps brand requirements while improving program clarity.",
      "Created digital learning assets and administered learning systems supporting internal training initiatives.",
      "Built intake tooling for LEOSA applicants, translating policy and eligibility requirements into clearer digital guidance.",
    ],
    approach: [
      "I worked end-to-end: understand the communication goal, choose the right medium, create the asset, and ensure it could actually be used effectively in the program’s operating environment.",
      "That hands-on ownership built a strong foundation in product thinking before I formally moved into service design and strategy roles.",
    ],
    contributions: [
      "Created recruiting and communications assets that improved the program’s visibility across physical and digital channels.",
      "Developed web experiences that made program information and application pathways easier to understand.",
      "Translated policy-heavy eligibility content into a digital intake experience that reduced confusion for applicants.",
      "Built early experience combining design, development, and operational problem-solving in one role.",
    ],
    outcomes: [
      "Enabled more consistent communications and recruiting support for the program across multiple channels.",
      "Improved clarity for users interacting with program information and application requirements online.",
      "Expanded my ownership from creative production into product and systems thinking.",
      "Established the multidisciplinary foundation that later supported enterprise transformation work.",
    ],
    reflection:
      "This period was important because it expanded my scope quickly. Owning design, development, and communications work at once taught me to think in systems long before I was doing enterprise transformation formally.",
  },
  CASCADE: {
    slug: "CASCADE",
    header: {
      descr: "PRODUCT OWNER",
      location: "INDEPENDENT",
      time: "2025 - PRESENT",
    },
    category: "AI systems / product ownership",
    heroImage: Gov,
    title: "CASCADE: AI-Driven Contractor Compliance Platform",
    overview: {
      organization: "Independent product development",
      product: "AI-driven compliance operations platform",
      timeframe: "2025 - Present",
      role: "Product Owner, System Architect & Developer",
      intro: "Designed an AI-driven compliance platform that interprets federal contract obligations and guides contractors from award intake through CAS disclosure and reporting preparation.",
      summary: "CASCADE is a full-stack compliance platform that interprets federal contract requirements and translates them into structured workflows for reporting, cost accounting, and CAS disclosure preparation. It combines public award ingestion, compliance ontology modeling, and AI-guided assistance to help contractors understand obligations, manage compliance operations, and generate required documentation through a unified system.",
    },
    problem: [
      "Federal contractors must interpret complex requirements across FAR, CAS, and agency-specific reporting frameworks to remain compliant. Preparing submissions such as Incurred Cost Proposals and CAS Disclosure Statements requires navigating disconnected data sources, spreadsheets, and regulatory guidance that is difficult to operationalize.",
      "Most organizations rely on consultants or internal specialists to translate obligations into reporting structures, making compliance slow, expensive, and error-prone. Smaller contractors in particular lack access to structured tooling that connects contract awards, cost structures, and required submissions into a coherent, repeatable compliance workflow.",
    ],
    challenge: [
      "Small and mid-sized federal contractors are required to produce sophisticated reporting artifacts such as Incurred Cost Proposals and CAS Disclosure Statements, but often lack the internal accounting infrastructure or regulatory expertise to do so confidently.",
      "Compliance requirements are documented across FAR, CAS, DCAA guidance, and agency-specific expectations, yet there is no unified system that translates these obligations into actionable workflows. The challenge was to design a platform that could interpret contract requirements, structure cost data correctly, and guide users through reporting preparation without requiring prior expertise in federal compliance frameworks.",
    ],
    responsibilities: [
      "Defined the system architecture, contract ingestion strategy, and compliance ontology used to represent reporting obligations across FAR and CAS frameworks.",
      "Designed and implemented an AI assistant layer trained to guide users through regulatory requirements such as Incurred Cost Proposals and CAS Disclosure Statements.",
      "Built obligation inference logic that translates contract characteristics into structured reporting expectations.",
      "Developed automated pathways for generating reporting structures from contract and financial inputs.",
      "Led ongoing platform evolution focused on expanding obligation mapping, improving reporting automation, and strengthening integrations with contractor cost data environments.",
    ],
    approach: [
      "Rather than treating compliance reporting as a document preparation exercise, CASCADE models regulatory obligations directly from contract awards and compliance thresholds. The system ingests publicly available award data and maps reporting requirements based on contract characteristics, CAS thresholds, and applicable FAR clauses.",
      "These obligations are then translated into structured workflows that guide users through preparing submissions such as Incurred Cost Proposals and Disclosure Statements. An assistant layer provides contextual regulatory guidance throughout the process, helping contractors interpret requirements without relying on external consultants or fragmented documentation.",
    ],
    contributions: [
      "Introduced an obligation-mapping architecture that infers reporting requirements directly from contract characteristics and regulatory thresholds rather than relying on manual configuration.",
      "Implemented an assistant-guided interpretation layer that helps contractors understand why specific reporting requirements apply, not just what to submit.",
      "Designed the assistant layer to guide users through preparing Incurred Cost Proposals and CAS Disclosure Statements using contextual regulatory references instead of static templates.",
      "Defined the ontology used to represent cost structures, reporting artifacts, and compliance dependencies to support future automated reporting scaffolds.",
      "Translated award data into structured compliance expectations across FAR and CAS frameworks, helping contractors understand submission requirements before reporting begins.",
    ],
    outcomes: [
      "CASCADE establishes a new approach to contractor compliance by translating regulatory requirements into structured, interpretable workflows instead of treating reporting as a manual documentation exercise.",
      "The platform currently infers reporting obligations from contract characteristics and provides guided preparation support for key submissions such as Incurred Cost Proposals and CAS Disclosure Statements.",
      "Ongoing development is expanding automated reporting scaffolds and integrations with contractor financial structures, positioning the system to reduce reliance on external consultants.",
      "Improves confidence for small and mid-sized contractors preparing their first compliance submissions.",
    ],
  },
  LE: {
    slug: "LE",
    header: {
      descr: "PERSONAL WORK",
      location: "INDEPENDENT RESEARCH & PROTOTYPING",
      time: "2017 - 2018",
    },
    category: "Innovation / product ownership",
    heroImage: LEHero,
    title: "Law Enforcement Accountability & Accreditation",
    overview: {
      organization: "Independent project",
      product: "Accountability and accreditation concept platform",
      timeframe: "2017 - 2018",
      role: "Product Owner, Researcher & Prototype Builder",
      intro:
        "Explored a product concept for law enforcement accountability through automated capture and structured data systems.",
      summary:
        "Defined and explored a concept platform for law-enforcement accountability, combining research, systems thinking, and prototyping to examine how automated capture and better data structures could support trust and oversight.",
    },
    problem: [
      "Use-of-force incidents and fragmented accountability processes created a visibility problem: important events could go unrecorded, evidence could be incomplete, and accreditation remained resource-intensive and difficult for many departments.",
      "I pursued this project to explore whether product thinking and systems design could reduce that opacity and create better conditions for accountability.",
    ],
    challenge: [
      "The topic was socially complex, technically sensitive, and tightly connected to human behavior, institutional incentives, and public trust.",
      "Any useful solution needed to address both event capture and the broader data ecosystem around training, accreditation, complaints, and organizational oversight.",
    ],
    responsibilities: [
      "Defined the product concept, research questions, and prototype direction independently.",
      "Conducted interviews and secondary research to understand body-worn camera adoption, resistance, and operational constraints.",
      "Designed and built working prototype elements to test automated recording triggers and supporting data concepts.",
      "Synthesized findings into a broader product vision that linked event capture to accreditation and accountability outcomes.",
    ],
    approach: [
      "I treated the work as an end-to-end product exploration: identify the policy and operational pain points, validate them through research, then build enough of a prototype to pressure-test whether the concept could work.",
      "Rather than stopping at a feature idea, I extended the concept into the wider system of records and incentives that would determine whether adoption was realistic.",
    ],
    contributions: [
      "Framed accountability as both a product and systems problem rather than a single-device problem.",
      "Designed a prototype concept for automatic recording triggered by officer equipment state changes.",
      "Extended the concept into an accreditation-supporting data model that connected training, incidents, complaints, and compliance information.",
      "Demonstrated end-to-end product ownership across research, concept design, prototyping, and narrative framing.",
    ],
    outcomes: [
      "Produced a working proof of concept that validated part of the automated capture idea.",
      "Clarified where technical feasibility was promising and where adoption would depend on trust, incentives, and organizational change.",
      "Strengthened my ability to use prototypes as strategic tools for exploring difficult public-sector problems.",
      "Showed early evidence of the AI and systems-thinking lens that now shapes my portfolio direction.",
    ],
    artifacts: [
      {
        title: "Prototype hardware setup",
        description:
          "A simple prototype linked a force-sensing trigger to a camera workflow to test automatic activation.",
        impact:
          "It turned a conceptual accountability idea into something concrete enough to evaluate.",
        image: bwcArduino,
        alt: "Arduino prototype",
      },
      {
        title: "Signal interpretation",
        description:
          "This screen showed the logic used to interpret the trigger state in the prototype.",
        impact:
          "It demonstrated how a physical signal could become an automated recording event.",
        image: bwcScreenshot,
        alt: "Prototype signal interpretation",
      },
      {
        title: "Activated capture test",
        description:
          "This artifact documented the prototype successfully activating the connected camera.",
        impact: "It validated a key part of the end-to-end concept.",
        image: bwcTest,
        alt: "Prototype capture test",
      },
    ],
    reflection:
      "Independent work like this sharpened my product instincts because I had to define the problem, choose the constraints, and decide what level of fidelity would create learning. That ownership is still central to how I lead.",
  },
  AS: {
    slug: "AS",
    header: {
      descr: "PERSONAL WORK",
      location: "INDEPENDENT RESEARCH & PROTOTYPING",
      time: "2018 - 2019",
    },
    category: "Innovation / AI systems thinking",
    heroImage: ASHero,
    title: "Active Shooter Detection & Environment Tracking Prototype",
    overview: {
      organization: "Independent project",
      product: "Computer-vision and spatial-awareness prototype",
      timeframe: "2018 - 2019",
      role: "Product Owner, Researcher & Prototype Builder",
      intro:
        "Developed a prototype combining computer vision and spatial modeling to improve real-time situational awareness.",
      summary:
        "Conceived and built a proof of concept that combined computer vision, environmental modeling, and alerting logic to explore whether responders could gain faster situational awareness during active shooter events.",
    },
    problem: [
      "In active shooter scenarios, responders often arrive with limited information about where the shooter is and how the environment is laid out. That lack of situational awareness slows response and increases risk.",
      "I pursued this project to test whether a technical system could narrow that information gap by linking automated detection to a live spatial representation of the environment.",
    ],
    challenge: [
      "The project required integrating multiple disciplines: machine vision, data collection, model training, hardware setup, 3D environmental modeling, and real-time visualization.",
      "It was also constrained by prototype-level resources, which meant making practical decisions about fidelity while still trying to learn whether the core concept could work.",
    ],
    responsibilities: [
      "Defined the concept, learning goals, and end-to-end system architecture for the prototype.",
      "Built data collection and training workflows for image detection, including scraper logic and classifier preparation.",
      "Created the 3D environment model and connected camera triggers to room-level visualization inside the prototype experience.",
      "Evaluated feasibility, surfaced limitations, and framed where the concept would need refinement for real-world use.",
    ],
    approach: [
      "I used an experimentation-first workflow: train a classifier, stand up a minimal camera network, model the environment, and connect those systems into a real-time response concept.",
      "The goal was not polish. It was to prove or disprove the value of combining AI-like detection workflows with a spatial interface that could support faster decision-making.",
    ],
    contributions: [
      "Built a custom image-scraping and preprocessing workflow to assemble initial training data for firearm detection.",
      "Trained and tested a classifier, then integrated the output into a room-level alerting system.",
      "Created a digital twin-style environment model in Blender and Unity to visualize where detection events were occurring.",
      "Used the prototype to identify both the promise and the limits of the concept, especially around accuracy and scalability.",
    ],
    outcomes: [
      "Produced a functioning proof of concept that linked detection events to room-level spatial alerts.",
      "Demonstrated how combining computer vision and environmental modeling could improve situational awareness for responders.",
      "Exposed critical next-step challenges around false positives, training data quality, and scalable environment capture.",
      "Strengthened my practice of using prototypes to explore AI-enabled product opportunities before overcommitting to full solutions.",
    ],
    reflection:
      "This project was an early expression of how I like to work with AI-adjacent systems: frame the operational problem clearly, build a prototype that tests the riskiest assumptions, and let the results guide the next strategic move.",
  },
};

export const homeSections = {
  deloitte: ["DeloitteAI"],
  currentWork: ["USAID", "DCMA", "NASS", "FNS"],
  foundational: ["SFFW", "MCCLEP"],
  personal: ["CASCADE", "LE", "AS"],
};
